import { useState, useEffect, useMemo } from "react";
import { BiUpload } from "react-icons/bi";
import { GrAscend, GrDescend } from "react-icons/gr";
import { PiMagnifyingGlass } from "react-icons/pi";
import { TbReload } from "react-icons/tb";
import NewFolderModal from "./NewFolderModal";
import { useLocation, useNavigate } from "react-router-dom";
import { LuFolderOpen } from "react-icons/lu";
import UploadFilesModal from "./UploadFilesModal";
import StorageTable from "./StorageTable";
import DeleteItemModal from "../Components/DeleteItemModal";
import { MdOutlineCreateNewFolder } from "react-icons/md";
import FormatDateTime from "../Functions/FormatDate";
import PopupMex from "../Components/SavePopup";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const FOLDER_CACHE_KEY = "e8_storage_folder_cache";

const MainStorage = ({
    account_id,
    userData,
    orgData,
    fetchOrgData
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate()
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [data, setDatata] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openFolderModal, setOpenFolderModal] = useState(false)
    const [openUploadFilesModal, setOpenUploadFilesModal] = useState(false)
    const [showDeleteItem, setShowDeleteItem] = useState(false)
    const [selectedItem, setSelectedItem] = useState(false)
    const [popupMex, setPopupMex] = useState({ active: false, type: null, message: "" })

    const [folderCache, setFolderCache] = useState(() => {
        const saved = localStorage.getItem(FOLDER_CACHE_KEY);
        return saved ? JSON.parse(saved) : {};
    });

    const location = useLocation();
    const path = useMemo(() => {
        const original_path = window.location.pathname;

        const cleanPath = original_path
            .replace("/e8console/storage", "")
            .replace(/^\/+/, "");

        return cleanPath ? `/${cleanPath}` : "/";
    }, [location.pathname]);


    const fetchData = async () => {
        if (!account_id) return;
        setLoading(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_folders_files`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: account_id,
                    path: path
                }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || "Failed to fetch");

            setDatata(result.data || []);
            const newCache = { ...folderCache };

            result.data.forEach(item => {
                if (item.type === "folder") {
                    newCache[item.id] = item.name;
                }
            });

            setFolderCache(newCache);
            localStorage.setItem(FOLDER_CACHE_KEY, JSON.stringify(newCache));
        } catch (err) {
            console.error("Error fetching members:", err);
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    const filteredAndSortedData = useMemo(() => {
        if (!data) return [];

        let result = data.filter((item) => {
            const term = searchTerm.toLowerCase();
            const nameMatch = item.name?.toLowerCase().includes(term);
            const local_created_at = FormatDateTime(item.created_at)
            const created_at = local_created_at.toLowerCase().includes(term);
            const status = item.status?.toLowerCase().includes(term);

            return nameMatch || created_at || status;
        });

        result.sort((a, b) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            return sortOrder === "asc"
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    const breadcrumb = useMemo(() => {
        if (!path || path === "/") return [];

        const ids = path.split("/").filter(Boolean);

        return ids.map(id => ({
            id,
            name: folderCache[id] || "..."
        }));
    }, [path, folderCache]);

    const downloadItem = async (item) => {
        if (item.type !== "file") return;

        try {
            const res = await fetch(`${GEN_BACKEND_LINK}/get_file_download_url`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: account_id,
                    file_id: item.id,
                }),
            });

            const data = await res.json();

            if (data.error == "file_not_found") {
                setPopupMex({ active: true, type: "error", message: "File doesn't exist..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    fetchData()
                }, 5000);
            }

            if (!res.ok) throw new Error(data.error);

            window.open(data.url, "_blank");

        } catch (err) {
            console.error("Download error:", err);
        }
    };

    const currentFolderId = useMemo(() => {
        if (!path || path === "/") return null;

        const parts = path.split("/").filter(Boolean);
        return parts.length ? parts[parts.length - 1] : null;
    }, [path]);

    // useEffect(() => {
    //     data.map((file) => {
    //         if (file.type == "file") {
    //             file.status == "UPLOADING" && setTimeout(() => {
    //                 fetchData()
    //             }, 5000);
    //         }
    //     })
    // }, [account_id, data]);

    useEffect(() => {
        fetchData();
        fetchOrgData()
    }, [account_id, path]);

    return (
        <div className="mainframe_item">
            <PopupMex active={popupMex.active} type={popupMex.type} message={popupMex.message} />
            <div className="mainframe_item_header">
                <p className="mainframe_item_title fade-in">{t.storage.title}</p>
                <div className="mainframe_item_subtitle fade-in">
                    <p>{t.storage.subtitle}</p>
                </div>
                <div className="storage_btn_cont">
                    <button className={`storage_btn ${loading ? "storage_btn_disabled" : ""}`} onClick={() => setOpenFolderModal(true)} disabled={loading}><i><MdOutlineCreateNewFolder /></i> {t.storage.new_folder}</button>
                    <button className={`storage_btn ${loading ? "storage_btn_disabled" : ""}`} onClick={() => setOpenUploadFilesModal(true)} disabled={loading}><i><BiUpload /></i> {t.storage.upload_files}</button>
                </div>

            </div>

            <div className="filter_body_mainframe">
                <div className="filtering_mainframe">
                    <div className="filtering_mainframe_btn_cont fade-in">
                        {data.length > 1 && (
                            <>
                                <button className={`filter_btn ${sortOrder === "asc" ? "active" : ""}`} onClick={() => setSortOrder("asc")} title="A-Z">
                                    <GrAscend />
                                </button>
                                <button className={`filter_btn ${sortOrder === "desc" ? "active" : ""}`} onClick={() => setSortOrder("desc")} title="Z-A">
                                    <GrDescend />
                                </button>
                            </>
                        )}
                        <button className="filter_btn" onClick={() => fetchData()} title={t.reload}>
                            <TbReload />
                        </button>
                    </div>
                    <div className="filter_input_cont fade-in">
                        <input
                            className="filter_input"
                            placeholder={t.search_filter}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                        />
                        <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                    </div>
                </div>
                <div className="path_item">
                    {path != "/" && (
                        <button
                            className="path_item_root_btn"
                            onClick={() => navigate("/e8console/storage/")}
                        >
                            <i><LuFolderOpen /></i>
                        </button>
                    )}
                    {breadcrumb.map((item, index) => (
                        <span key={item.id}>
                            <span className="breadcrumb_sep"> / </span>

                            <button
                                className="path_item_root_btn"
                                title={item.name}
                                onClick={() =>
                                    navigate(
                                        "/e8console/storage/" +
                                        breadcrumb.slice(0, index + 1).map(i => i.id).join("/")
                                    )
                                }
                            >
                                {item.name}
                            </button>
                        </span>
                    ))}
                </div>
                <StorageTable
                    loading={loading}
                    data={filteredAndSortedData}
                    setSelectedItem={setSelectedItem}
                    setShowDeleteItem={setShowDeleteItem}
                    downloadItem={downloadItem}
                    orgData={orgData}
                />
            </div>

            <NewFolderModal
                account_id={account_id}
                userData={userData}
                show={openFolderModal}
                handleClose={() => setOpenFolderModal(false)}
                fetchRecords={fetchData}
                path={path}
                orgData={orgData}
                fetchOrgData={fetchOrgData}
            />

            <UploadFilesModal
                account_id={account_id}
                userData={userData}
                show={openUploadFilesModal}
                handleClose={() => setOpenUploadFilesModal(false)}
                fetchRecords={fetchData}
                path={path}
                currentFolderId={currentFolderId}
                orgData={orgData}
                fetchOrgData={fetchOrgData}
            />

            <DeleteItemModal
                account_id={account_id}
                show={showDeleteItem}
                handleClose={() => setShowDeleteItem(false)}
                fetchRecords={fetchData}
                item={selectedItem}
                endpoint={`delete_${selectedItem.type}`}
                title={`Delete ${selectedItem.type}`}
                description={
                    selectedItem.type === "folder"
                        ? t.storage.delete_folder
                        : t.storage.delete_file
                }
                input_value={selectedItem}
            />
        </div>
    );
};

export default MainStorage;